import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import {
  OrderableItem,
  OrderedItem,
  OrderableIngredient,
  OrderedIngredient,
  ImageAlternativeType,
  Picture,
  IngredientChoice,
} from 'ngx-web-api';
import { StagePosition } from './domain/stage-position.enum';
import { StageIngredient } from './domain/stage-ingredient.model';
import { StageIngredientAction } from './domain/stage-ingredient-action.model';
import { IngredientAction } from './domain/ingredient-action.enum';
import { MenuWrapperService } from '../core/services/menu-wrapper.service';

@Injectable({
  providedIn: 'root',
})
export class BuilderService {
  private stageUpdate: Subject<StageIngredientAction> = new Subject<StageIngredientAction>();
  public stageUpdate$: Observable<StageIngredientAction> = this.stageUpdate.asObservable();

  private builderImageLoading: Subject<boolean> = new Subject<boolean>();
  public builderImageLoading$: Observable<boolean> = this.builderImageLoading.asObservable();

  private restoreDone = false;

  constructor(private menuService: MenuWrapperService) {}

  public restoreStageIngredients(ordered: OrderedItem, orderable: OrderableItem) {
    if (this.restoreDone) {
      return;
    }
    orderable.choices.forEach((choice: IngredientChoice) => {
      const orderedChoice = ordered.getChoice(choice.name);
      if (!orderedChoice) {
        return;
      } else {
        (orderedChoice.ingredients || []).forEach((ingredient: OrderedIngredient) => {
          const orderableIngredient = choice.ingredients.find((ing: OrderableIngredient) => ing.name === ingredient.ingredient);
          if (!!orderableIngredient) {
            const orderedQualifiers =
              !!ingredient.qualifiers && ingredient.qualifiers.map(q => orderableIngredient.qualifiers.find(n => n.name === q));
            const tempAction: StageIngredientAction = {
              action: IngredientAction.RESTORE,
              left: ingredient.isLeftHalf,
              right: ingredient.isRightHalf,
              name: ingredient.ingredient,
              orderedQualifierList: ingredient.qualifiers,
              qualifier: !!orderedQualifiers && orderedQualifiers.find(q => q.recipeFactor !== 0),
            };
            const restoreAction: StageIngredientAction = {
              stageIngredient: this.getStageIngredientFromOrderable(tempAction, orderableIngredient),
              ...tempAction,
            };
            this.updateStagedIngredient(restoreAction);
          }
        });
      }
    });
    this.restoreDone = true;
  }

  public updateStagedIngredient(ingredientAction: StageIngredientAction) {
    this.stageUpdate.next(ingredientAction);
  }

  public updateStagedIngredientByOrderable(ingredientAction: StageIngredientAction) {
    this.stageUpdate.next({
      ...ingredientAction,
      stageIngredient: this.getStageIngredientFromOrderable(ingredientAction),
    });
  }

  getStageIngredientFromOrderable(ingredientAction: StageIngredientAction, orderable?: OrderableIngredient): StageIngredient {
    if (orderable) {
      ingredientAction.orderableIngredient = orderable;
    }
    let builderImage: Picture = this.menuService.getPicture(
      ingredientAction.orderableIngredient.pictures,
      ImageAlternativeType.BUILDER_REPEAT,
      null
    );

    if (!builderImage) {
      const defaultQualifier = ingredientAction.orderableIngredient.qualifiers?.find(q => q.isDefault);
      if (defaultQualifier && (ingredientAction.action === IngredientAction.ADD || ingredientAction.action === IngredientAction.RESTORE)) {
        builderImage = this.menuService.getPicture(defaultQualifier.pictures, ImageAlternativeType.BUILDER_FIXED_QUALIFIER, null);
      }

      if (!builderImage) {
        // Existing logic for other cases
        if (ingredientAction.action !== IngredientAction.REMOVE_QUALIFIER && ingredientAction.qualifier) {
          let qualType = ImageAlternativeType.BUILDER_FIXED_QUALIFIER;
          if (ingredientAction.left) {
            qualType = ImageAlternativeType.BUILDER_FIXED_QUALIFIER_LEFT_HALF;
          } else if (ingredientAction.right) {
            qualType = ImageAlternativeType.BUILDER_FIXED_QUALIFIER_RIGHT_HALF;
          }
          builderImage = this.menuService.getPicture(ingredientAction.qualifier.pictures, qualType, null);
        } else if (
          !ingredientAction.qualifier ||
          ingredientAction.qualifier.recipeFactor !== 0 ||
          ingredientAction.qualifier.type === 'Side'
        ) {
          if (ingredientAction.left || ingredientAction.right) {
            builderImage = this.menuService.getPicture(
              ingredientAction.orderableIngredient.pictures,
              ingredientAction.left ? ImageAlternativeType.BUILDER_FIXED_LEFT_HALF : ImageAlternativeType.BUILDER_FIXED_RIGHT_HALF,
              null
            );
          } else {
            builderImage = this.menuService.getPicture(
              ingredientAction.orderableIngredient.pictures,
              ImageAlternativeType.BUILDER_FIXED,
              null
            );
          }
        }
      }
    }

    return {
      name: ingredientAction.orderableIngredient.name,
      position: StagePosition.DEFAULT,
      included: false,
      src: !!builderImage ? builderImage.src : undefined,
      image: !!builderImage ? this.createImageElement(builderImage.src) : undefined,
      imageType: !!builderImage ? builderImage.type : undefined,
      ordinal: ingredientAction.orderableIngredient.stackingOrder,
      qualifierList: ingredientAction.orderableIngredient.qualifiers,
    };
  }

  createImageElement(imageSrc: string): HTMLImageElement {
    const image: HTMLImageElement = new Image();
    image.src = imageSrc;
    return image;
  }

  resetStageAction() {
    this.restoreDone = false;
    this.stageUpdate.next({ action: IngredientAction.RESET });
  }

  startBuilderImageLoading() {
    this.builderImageLoading.next(true);
  }

  stopBuilderImageLoading() {
    this.builderImageLoading.next(false);
  }
}
